import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaSave, FaImage, FaTimes } from 'react-icons/fa';
import { vehicleAPI, resolveMediaUrl } from '../../services/api';
import './VehicleForm.css';

const VehicleForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [imagePreview, setImagePreview] = useState([]);
  const [formData, setFormData] = useState({
    make: '',
    model: '',
    year: new Date().getFullYear(),
    price: '',
    mileage: '',
    transmission: 'Automatic',
    fuelType: 'Petrol',
    bodyType: 'Sedan',
    features: '',
    description: '',
    status: 'available',
    featured: false,
    badge: '',
    images: [],
    existingImages: []
  });

  const fetchVehicle = useCallback(async () => {
    try {
      const response = await vehicleAPI.getById(id);
      if (response.data.success) {
        const vehicle = response.data.data;
        setFormData({
          make: vehicle.make,
          model: vehicle.model,
          year: vehicle.year,
          price: vehicle.price,
          mileage: vehicle.mileage,
          transmission: vehicle.transmission,
          fuelType: vehicle.fuelType,
          bodyType: vehicle.bodyType,
          features: Array.isArray(vehicle.features) ? vehicle.features.join(', ') : vehicle.features,
          description: vehicle.description || '',
          status: vehicle.status,
          featured: vehicle.featured,
          badge: vehicle.badge || '',
          images: [],
          existingImages: vehicle.images || []
        });
        setImagePreview(vehicle.images || []);
      }
    } catch (error) {
      console.error('Error fetching vehicle:', error);
      setError(error.response?.data?.message || 'Failed to fetch vehicle details');
    }
  }, [id]);

  useEffect(() => {
    if (isEdit) {
      fetchVehicle();
    }
  }, [fetchVehicle, isEdit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData({
      ...formData,
      images: [...formData.images, ...files]
    });

    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    const isExisting = index < formData.existingImages.length;

    if (isExisting) {
      setFormData({
        ...formData,
        existingImages: formData.existingImages.filter((_, i) => i !== index)
      });
    } else {
      const newImageIndex = index - formData.existingImages.length;
      setFormData({
        ...formData,
        images: formData.images.filter((_, i) => i !== newImageIndex)
      });
    }

    setImagePreview(imagePreview.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const submitData = new FormData();

      submitData.append('make', formData.make);
      submitData.append('model', formData.model);
      submitData.append('year', formData.year);
      submitData.append('price', formData.price);
      submitData.append('mileage', formData.mileage);
      submitData.append('transmission', formData.transmission);
      submitData.append('fuelType', formData.fuelType);
      submitData.append('bodyType', formData.bodyType);
      submitData.append('features', formData.features);
      submitData.append('description', formData.description);
      submitData.append('status', formData.status);
      submitData.append('featured', formData.featured);
      submitData.append('badge', formData.badge);

      if (isEdit) {
        submitData.append('existingImages', JSON.stringify(formData.existingImages));
      }

      formData.images.forEach((image) => {
        submitData.append('images', image);
      });

      const response = isEdit
        ? await vehicleAPI.update(id, submitData)
        : await vehicleAPI.create(submitData);

      if (response.data.success) {
        setSuccess(isEdit ? 'Vehicle updated successfully!' : 'Vehicle added successfully!');
        setTimeout(() => {
          navigate('/admin/dashboard');
        }, 1500);
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save vehicle');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="vehicle-form-container">
      <div className="vehicle-form-header">
        <motion.button
          className="btn-back"
          onClick={() => navigate('/admin/dashboard')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FaArrowLeft /> Back to Dashboard
        </motion.button>
        <h1>{isEdit ? 'Edit Vehicle' : 'Add New Vehicle'}</h1>
      </div>

      {error && (
        <motion.div
          className="alert alert-error"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {error}
        </motion.div>
      )}

      {success && (
        <motion.div
          className="alert alert-success"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {success}
        </motion.div>
      )}

      <form onSubmit={handleSubmit} className="vehicle-form">
        <div className="form-section">
          <h2>Basic Information</h2>
          <div className="form-grid">
            <div className="form-field">
              <label htmlFor="make">Make *</label>
              <input
                type="text"
                id="make"
                name="make"
                value={formData.make}
                onChange={handleChange}
                required
                placeholder="e.g., Toyota"
              />
            </div>

            <div className="form-field">
              <label htmlFor="model">Model *</label>
              <input
                type="text"
                id="model"
                name="model"
                value={formData.model}
                onChange={handleChange}
                required
                placeholder="e.g., Corolla"
              />
            </div>

            <div className="form-field">
              <label htmlFor="year">Year *</label>
              <input
                type="number"
                id="year"
                name="year"
                value={formData.year}
                onChange={handleChange}
                required
                min="1990"
                max={new Date().getFullYear() + 1}
              />
            </div>

            <div className="form-field">
              <label htmlFor="price">Price (LKR) *</label>
              <input
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                required
                min="0"
                placeholder="e.g., 5500000"
              />
            </div>

            <div className="form-field">
              <label htmlFor="mileage">Mileage (km) *</label>
              <input
                type="number"
                id="mileage"
                name="mileage"
                value={formData.mileage}
                onChange={handleChange}
                required
                min="0"
                placeholder="e.g., 45000"
              />
            </div>

            <div className="form-field">
              <label htmlFor="bodyType">Body Type *</label>
              <select
                id="bodyType"
                name="bodyType"
                value={formData.bodyType}
                onChange={handleChange}
                required
              >
                <option value="Sedan">Sedan</option>
                <option value="SUV">SUV</option>
                <option value="Hatchback">Hatchback</option>
                <option value="Van">Van</option>
                <option value="Coupe">Coupe</option>
                <option value="Wagon">Wagon</option>
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Technical Specifications</h2>
          <div className="form-grid">
            <div className="form-field">
              <label htmlFor="transmission">Transmission *</label>
              <select
                id="transmission"
                name="transmission"
                value={formData.transmission}
                onChange={handleChange}
                required
              >
                <option value="Automatic">Automatic</option>
                <option value="Manual">Manual</option>
                <option value="CVT">CVT</option>
                <option value="Semi-Automatic">Semi-Automatic</option>
              </select>
            </div>

            <div className="form-field">
              <label htmlFor="fuelType">Fuel Type *</label>
              <select
                id="fuelType"
                name="fuelType"
                value={formData.fuelType}
                onChange={handleChange}
                required
              >
                <option value="Petrol">Petrol</option>
                <option value="Diesel">Diesel</option>
                <option value="Hybrid">Hybrid</option>
                <option value="Electric">Electric</option>
              </select>
            </div>

            <div className="form-field">
              <label htmlFor="status">Status *</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                required
              >
                <option value="available">Available</option>
                <option value="sold">Sold</option>
                <option value="reserved">Reserved</option>
              </select>
            </div>

            <div className="form-field">
              <label htmlFor="badge">Badge (optional)</label>
              <input
                type="text"
                id="badge"
                name="badge"
                value={formData.badge}
                onChange={handleChange}
                placeholder="e.g., New Arrival, Best Deal"
              />
            </div>
          </div>

          <div className="form-field checkbox-field">
            <label>
              <input
                type="checkbox"
                name="featured"
                checked={formData.featured}
                onChange={handleChange}
              />
              <span>Mark as Featured Vehicle</span>
            </label>
          </div>
        </div>

        <div className="form-section">
          <h2>Features & Description</h2>
          <div className="form-field">
            <label htmlFor="features">Features (comma-separated)</label>
            <input
              type="text"
              id="features"
              name="features"
              value={formData.features}
              onChange={handleChange}
              placeholder="e.g., Push Start, Leather Seats, Sunroof, Alloy Wheels"
            />
            <small>Separate each feature with a comma</small>
          </div>

          <div className="form-field">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="5"
              placeholder="Provide a detailed description of the vehicle..."
            />
          </div>
        </div>

        <div className="form-section">
          <h2>Images</h2>
          <div className="image-upload-container">
            <label htmlFor="images" className="image-upload-label">
              <FaImage />
              <span>Click to upload images</span>
              <small>Maximum 10 images, 5MB each</small>
            </label>
            <input
              type="file"
              id="images"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              multiple
              onChange={handleImageChange}
              style={{ display: 'none' }}
            />
          </div>

          {imagePreview.length > 0 && (
            <div className="image-preview-grid">
              {imagePreview.map((image, index) => (
                <div key={index} className="image-preview-item">
                  <img
                    src={resolveMediaUrl(image)}
                    alt={`Preview ${index + 1}`}
                  />
                  <button
                    type="button"
                    className="remove-image-btn"
                    onClick={() => removeImage(index)}
                  >
                    <FaTimes />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn-cancel-form"
            onClick={() => navigate('/admin/dashboard')}
          >
            Cancel
          </button>
          <motion.button
            type="submit"
            className="btn-submit"
            disabled={loading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FaSave /> {loading ? 'Saving...' : (isEdit ? 'Update Vehicle' : 'Add Vehicle')}
          </motion.button>
        </div>
      </form>
    </div>
  );
};

export default VehicleForm;
